import { useContext, useState } from "react";
import Button, { ButtonType } from "../../../components/Button/Button";
import Toast from "../../../components/Toast/Toast";
import { Context, IContext } from "../../../context/ContextApp";
import PlusIcon from "../../../assets/plus";
import SectionCard from "../../../components/SectionCard/SectionCard";

import NewAddress from "./NewAddress/NewAddress";
import EditAddressForm from "./EditAddress/EditAddressForm";
import AddressBookList from "./AddressBookList";
import { useAddressBook } from "./hooks/useAddressBook";
import { useAddressForm } from "./hooks/useAddressForm";
import AddressBook from "../../../assets/addressBook";

enum ViewMode {
  List = "list",
  New = "new",
  Edit = "edit",
}

const AddressBookContainer = () => {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.List);
  const { appBranch: branch }: IContext = useContext(Context);

  // Custom hooks for address management
  const { savedAddresses, isLoading, toast, setToast, fetchAddresses, deleteAddress } =
    useAddressBook();

  // Custom hooks for form management
  const {
    selectedAddress,
    form,
    isFormValid,
    initializeEdit,
    resetForm,
    handleFormChange,
    handleAddressChange,
    updateAddress,
  } = useAddressForm({
    branch,
    onSuccess: () => {
      setViewMode(ViewMode.List);
      fetchAddresses();
    },
    setToast,
  });

  const handleEditAddress = (address: typeof selectedAddress) => {
    if (address) {
      initializeEdit(address);
      setViewMode(ViewMode.Edit);
    }
  };

  const handleBackToList = () => {
    resetForm();
    setViewMode(ViewMode.List);
    fetchAddresses();
  };

  return (
    <SectionCard
      title="Address Book"
      icon={<AddressBook width={20} height={20} />}
      titleSuffix={
        <>
          {viewMode === ViewMode.New && <span> - New Address</span>}
          {viewMode === ViewMode.Edit && <span> - Edit Address</span>}
        </>
      }
      headerAction={
        viewMode === ViewMode.List ? (
          <Button
            type={ButtonType.secondary}
            onClick={() => setViewMode(ViewMode.New)}
            className="address-book-toggle-button"
          >
            <PlusIcon width={16} height={16} />
            <span>Add New Address</span>
          </Button>
        ) : undefined
      }
      footer={<Toast content={toast} onClick={() => setToast(null)} />}
    >
      {viewMode === ViewMode.List && (
        <AddressBookList
          savedAddresses={savedAddresses}
          isLoading={isLoading}
          onEditAddress={handleEditAddress}
          onDeleteAddress={deleteAddress}
        />
      )}
      {viewMode === ViewMode.New && <NewAddress setEditMode={handleBackToList} />}
      {viewMode === ViewMode.Edit && selectedAddress && (
        <EditAddressForm
          selectedAddress={selectedAddress}
          branch={branch}
          form={form}
          isFormValid={isFormValid()}
          onFormChange={handleFormChange}
          onAddressChange={handleAddressChange}
          onSave={updateAddress}
          onCancel={handleBackToList}
        />
      )}
    </SectionCard>
  );
};

export default AddressBookContainer;
